import type { InferResponse } from '@/hooks/useApiClient'
import { CheckCircleOutlined, ClockCircleOutlined, DeleteOutlined } from '@ant-design/icons'
import { useSiteMonitoring } from '@api/useSiteMonitoring'
import { defaultDateDef, defaultNumberDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { getDefaultDateComparator } from '@utils/grid'
import { type DateOrMoment, formatters, type Timezone } from '@utils/intl/dateTimeFormatters'
import { Button, Popconfirm, Tooltip } from 'antd'

type TankReading = { _id: string; source: string; processed: boolean } & InferResponse<'/store/tank_readings/overview'>

function ProcessedCellRenderer({ data }: { data: TankReading }) {
  return data?.processed ? (
    <CheckCircleOutlined style={{ color: 'green' }} />
  ) : (
    <ClockCircleOutlined style={{ color: 'orange' }} />
  )
}

export function getColumnDefs(storeTimezone: Timezone) {
  const siteTzFormatter = (dateOrMoment: DateOrMoment) => formatters.DATE_TIME(dateOrMoment, storeTimezone, true)
  return [
    { field: 'store_number', headerName: 'Store Number' },
    { field: 'tank_id', headerName: 'Tank ID' },
    {
      field: 'volume',
      headerName: 'Volume',
      ...defaultNumberDef('volume', 0),
    },
    {
      field: 'read_time',
      headerName: 'Measured Time',
      ...defaultDateDef('read_time', siteTzFormatter, false, null, storeTimezone),
      comparator: getDefaultDateComparator(formatters.DATE_TIME),
    },
    {
      field: 'run_time',
      headerName: 'Received Time',
      ...defaultDateDef('run_time', siteTzFormatter, false, null, storeTimezone),
      comparator: getDefaultDateComparator(formatters.DATE_TIME),
    },
    {
      field: 'processed',
      headerName: 'Processed',
      cellRenderer: ProcessedCellRenderer,
    },
    { field: 'source', headerName: 'Source' },
    {
      headerName: '',
      field: '_actions',
      maxWidth: 48,
      cellRenderer: ({ data }: { data: TankReading }) => {
        // show delete for manual readings only
        const isManual = data?.source && String(data.source).toLowerCase().includes('manual')
        if (!isManual) return null
        return <DeleteManualCell data={data} />
      },
    },
  ]
}

function DeleteManualCell({ data }: React.PropsWithChildren<{ data: TankReading }>) {
  const { useDeleteManualReadingMutation } = useSiteMonitoring()

  const handleConfirm = async () => {
    if (!data?._id) return
    useDeleteManualReadingMutation.mutateAsync({ reading_id: data?._id })
  }

  return (
    <Popconfirm
      title='Are you sure you want to delete this reading?'
      onConfirm={handleConfirm}
      okText='Yes'
      cancelText='Cancel'
    >
      <Tooltip title='Delete reading'>
        <Button type='text' icon={<DeleteOutlined />} />
      </Tooltip>
    </Popconfirm>
  )
}
